import React from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, Typography } from "antd";
import styled from "styled-components";

import ACTIONS from "../../store/action";
import ErrorMessage from "../customComponent/customMessages/ErrorMessage";
import { GRAY_50, GRAY_300, GRAY_700, PRIMARY_600, GRAY_400, GRAY_500 } from "../../constant/colors";
import { ColumnsKeys, SelectedReports } from "../../constant/filters";
import { useDispatch, useSelector } from "react-redux";
import { selectReportColumns } from "../../store/selector";
import { fontWeights, sizes } from "../../constant/styles";

const { Text } = Typography;

const StyledWrapper = styled.div`
  background: ${GRAY_50};
  border-radius: 6px;
  padding: 16px 20px;
  margin-top: 10px;
  margin-left: 24px;
`;

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const StyledHeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StyledHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  white-space: nowrap;
`;

const StyledLink = styled.span`
  color: ${PRIMARY_600};
  font-weight: 700;
  font-size: 12px;
  cursor: pointer;
`;

const StyledDivider = styled.span`
  width: 1px;
  height: 20px;
  background: ${GRAY_300};
`;

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
`;

const reportTitleKeys = {
  enrollments: "data_types.enrollments_type",
  enrollment_claims: "data_types.claims_per_enrollment_type",
  claims: "columns.claim_breakdown",
  claim_line_items: "columns.line_items_breakdown",
  orders: "columns.order_breakdown",
  order_line_items: "columns.order_line_items_breakdown",
  completed_orders: "data_types.completed_order_type"
};

const ColumnSelector = ({ selectedReport }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const setReportsColumnsAct = (reportColumns) => dispatch(ACTIONS.setReportsColumns(reportColumns));
  const reportColumns = useSelector(selectReportColumns);

  const newSelectedView = selectedReport || SelectedReports.enrollments;
  const columnKeys = Object.keys(reportColumns[newSelectedView]);
  const columns = Object.values(reportColumns[newSelectedView]);
  const totalColumns = columns.length;
  const selectedColumns = columns.filter((col) => col.display).length;

  const handleMenuClick = (e) => {
    const currColumn = columns[e.key];
    const objectKey = columnKeys[e.key];
    const newColumn = { ...currColumn, display: !currColumn.display };
    const newTableColumns = {
      ...reportColumns,
      [newSelectedView]: {
        ...reportColumns[newSelectedView],
        [objectKey]: newColumn
      }
    };

    try {
      localStorage.setItem(
        ColumnsKeys.reports[newSelectedView],
        JSON.stringify({ ...reportColumns[newSelectedView], [objectKey]: newColumn })
      );
    } catch (err) {
      ErrorMessage(`Could not save column selection: ${err}`);
    }

    setReportsColumnsAct(newTableColumns);
  };

  const handleSelectAll = () => {
    const updatedColumns = {};
    Object.entries(reportColumns[newSelectedView]).forEach(([key, col]) => {
      updatedColumns[key] = { ...col, display: true };
    });
    const newTableColumns = {
      ...reportColumns,
      [newSelectedView]: updatedColumns
    };
    try {
      localStorage.setItem(ColumnsKeys.reports[newSelectedView], JSON.stringify(updatedColumns));
    } catch (err) {
      ErrorMessage(`Could not save column selection: ${err}`);
    }
    setReportsColumnsAct(newTableColumns);
  };

  const handleClearAll = () => {
    const updatedColumns = {};
    Object.entries(reportColumns[newSelectedView]).forEach(([key, col]) => {
      updatedColumns[key] = { ...col, display: col.mandatory };
    });
    const newTableColumns = {
      ...reportColumns,
      [newSelectedView]: updatedColumns
    };
    try {
      localStorage.setItem(ColumnsKeys.reports[newSelectedView], JSON.stringify(updatedColumns));
    } catch (err) {
      ErrorMessage(`Could not save column selection: ${err}`);
    }
    setReportsColumnsAct(newTableColumns);
  };

  const titleKey = reportTitleKeys[selectedReport] || reportTitleKeys.enrollments;

  return (
    <StyledWrapper>
      <StyledHeader>
        <StyledHeaderLeft>
          <Text strong style={{ fontSize: 14, color: GRAY_700 }}>
            {t(titleKey)}
          </Text>
          <Text style={{ fontSize: 13, color: GRAY_500 }}>{t("columns.description")}</Text>
        </StyledHeaderLeft>
        <StyledHeaderRight>
          <Text style={{ fontSize: sizes.small, color: GRAY_400, fontWeight: fontWeights.bold }}>
            {t("columns.columns_selected", { selected: selectedColumns, total: totalColumns })}
          </Text>
          <StyledDivider />
          <StyledLink id={`${selectedReport}-select-all`} onClick={handleSelectAll}>
            {t("select_all")}
          </StyledLink>
          <StyledDivider />
          <StyledLink id={`${selectedReport}-clear-all`} onClick={handleClearAll}>
            {t("columns.clear_all")}
          </StyledLink>
        </StyledHeaderRight>
      </StyledHeader>
      <StyledGrid>
        {columns.map((col, index) => (
          <label
            key={index}
            id={`column_selector_${col.key}`}
            style={{ cursor: col.mandatory ? "not-allowed" : "pointer" }}
          >
            <Checkbox disabled={col.mandatory} checked={col.display} onChange={() => handleMenuClick({ key: index })} />
            <Text disabled={col.mandatory} style={{ marginLeft: 8 }}>
              {col.title}
            </Text>
          </label>
        ))}
      </StyledGrid>
    </StyledWrapper>
  );
};

export { ColumnSelector };
